import { useEffect, useState } from "react";
import { SubmitButton } from "../buttons/Buttons";
import { MfaHelpDrawer } from "../shared-components/MfaHelpDrawer";
import { useHistory } from "react-router";
import { TextField } from "@cmsgov/ds-medicare-gov";
import H1 from "../../common/components/h1";
import Div from "../../common/components/div";
import { MfaEnrollStep1Response, useMfaApi } from "../hooks/useMfaApi";
import translate from "../../common/functions/translate";
import { validatePhone } from "../validators/mfaSchema";
import { AxiosResponse } from "axios";
import { SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import { useMfaInlineError } from "../../common/hooks/use.analytics";
import { VerifyPassword } from "../VerifyPassword";
import capitalizeFirstLetter from "../../common/functions/capitalizeFirstLetter";
import { reviewTermsAndConditionsAndPrivacyPolicy } from "../../common/components/MfaShared";
import { getMfaDuplicateChannelError, hasErrorMessage } from "../../common/functions/getApiErrorMessage";

interface EnterPhoneProps {
    type: 'sms' | 'voice'
    flow: 'add' | 'enroll'
}

export const EnterPhone = ({ type, flow }: EnterPhoneProps) => {
    const [fieldValue, setFieldValue] = useState('');
    const [phoneRef, setPhoneRef] = useState<HTMLElement | null>(null);
    const [fieldErrorText, setFieldErrorText] = useState('');
    const [showFieldError, setShowFieldError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState<AxiosResponse<MfaEnrollStep1Response> | null>(null);
    const [isVerified, setIsVerified] = useState(true);
    const inputLabel = translate(`phone_number_${type}`) +  ' (' + translate("us_numbers_only") + ')';
    const inlineErr = useMfaInlineError(inputLabel);

    const history = useHistory();
    const api = useMfaApi();

    useEffect(() => {
        const errorKeys = validatePhone(fieldValue, type);
        setFieldErrorText(errorKeys ? translate(errorKeys[0]) : '');
    }, [fieldValue, type]);

    async function onSubmit(event?: React.FormEvent<HTMLFormElement>) {
        event?.preventDefault();
        setShowFieldError(true);
        if (fieldErrorText || isLoading) {
            if (fieldErrorText) {
                inlineErr.validationError(fieldErrorText);
            }
            return;
        }
        setIsLoading(true);
        setApiResponse(null);
        const mfaStepResponse = await api.mfaEnrollStep1({
            type: type,
            value: fieldValue
        });
        const code = mfaStepResponse?.data.code;
        setIsLoading(false);
        if (code === 401) {
            setIsVerified(false);
            return;
        }
        setApiResponse(mfaStepResponse);
        const errors = mfaStepResponse?.data.errors || [];
        if (code === 400 && hasErrorMessage(errors, 'MFA_NON_US_PHONE_NUMBER')) {
            const errMessage = translate(`non_us_number_${type}`);
            setFieldErrorText(errMessage);
            inlineErr.serverError(errMessage, 'MFA_NON_US_PHONE_NUMBER');
        } else {
            // set inline error for duplicate number if applicable
            const duplicateErr = getMfaDuplicateChannelError(errors, type);
            setFieldErrorText(duplicateErr);
            if (duplicateErr) {
                inlineErr.serverError(duplicateErr, 'MFA_DUPLICATE_CHANNEL');
            }
        }
        if (code === 201) {
            history.push('/security-code/enroll/confirm-your-method', {
                type: type,
                value: fieldValue,
                flow: flow || null
            });
        }
    }

    function updateFieldValue(value: string) {
        setFieldValue(value.replace(/-/g, ""));
        if (showFieldError) {
            setShowFieldError(false);
        }
    }

    const buttonText = translate(`send_my_code_${type}`);
    const errorFlow = `MfaEnrollStep1${capitalizeFirstLetter(type)}` as const;
    if (!isVerified) {
        return <VerifyPassword type={type} flow={flow} onVerified={onSubmit} />;
    }

    const headingTitle = translate(`mfa_title_${flow}`);
    const h2Text = translate(`enter_your_phone_to_get_code_${type}`);
    return (
        <>
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1 className={'ds-u-margin-bottom--1'}>{headingTitle}</H1>
                        <MfaHelpDrawer />
                    </Div>
                </Div>
                <SmartErrorAlert
                    className="ds-u-margin-top--0 ds-u-margin-bottom--3"
                    name={errorFlow}
                    fieldRef={null}
                    apiResponse={apiResponse}
                    conversion={null}
                    invalidFields={null}
                    onInvalidField={() => {}}
                    parentHeading={h2Text}
                />
                <Div isRow>
                    <Div cols={12} colsMD={8}>
                        <h2 className={"ds-text-heading--xl"}>{h2Text}</h2>
                    </Div>
                </Div>
                {(type === 'sms') &&  (
                    <Div isRow>
                        <Div cols={12} colsMD={8}>
                            <p className="ds-u-margin-top--1 ds-u-margin-bottom--1">{translate("receive_text_messages")}</p>
                            <p className="ds-u-margin-top--1 ds-u-margin-bottom--1">{translate("message_rates_apply")}</p>
                            <p className="ds-u-margin-top--1 ds-u-margin-bottom--1">
                                {reviewTermsAndConditionsAndPrivacyPolicy(headingTitle)}
                            </p>
                        </Div>
                    </Div>
                )}
                <Div isRow>
                    <Div cols={12} colsMD={8}>
                        <TextField
                            labelClassName="ds-u-margin-top--1"
                            className="ds-u-margin-bottom--3"
                            aria-invalid={!!(showFieldError && fieldErrorText)}
                            errorMessage={showFieldError ? fieldErrorText : null}
                            inputRef={setPhoneRef}
                            label={inputLabel}
                            name={'phone'}
                            onChange={event => updateFieldValue(event.currentTarget.value)}
                            onBlur={() => setShowFieldError(true)}
                            value={fieldValue}
                            size="medium"
                            mask="phone"
                            hint="XXX-XXX-XXXX"
                        />
                    </Div>
                </Div>
                <SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                    text: buttonText,
                    parentComponentHeading: h2Text
                }}>
                    {buttonText}
                </SubmitButton>
            </form>
        </>
    );
}